import { useContext, useEffect, useState } from "react";
import { FORMNAMES, setBrowserTitle } from "../utilities/codes";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { schoolContext } from "../contexts/SchoolContextProvider";
import { getFormEntriesByFormName } from "../services/formServices";
import { FaArrowRight, FaBriefcase, FaGraduationCap, FaTrophy } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { I18N_KEYS } from "../languages/PlaceHolder.js";
import { useNavigate } from "react-router-dom";
import { mediaSource } from "../utilities/mediautilities.js";
import Breadcrumb from "./Breadcrumb.jsx";
import { MdEmail } from "react-icons/md";

const TopTeachers = ({ isTopTeacher = false }) => {
    const [topTeachers, setTopTeachers] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const { schoolDetails } = useContext(schoolContext);
    const { t } = useTranslation()
    const navigate = useNavigate();

    useEffect(() => {
        setBrowserTitle("Our Teacher - Honors")
    }, [])

    useEffect(() => {
        getTopTeachers();
    }, []);

    const getTopTeachers = async () => {
        let filters = { staff_role: { operator: "equals", value: "Teacher" } }
        try {
            if (isTopTeacher) {
                filters = { ...filters, top_teacher: { operator: "equals", value: true } };
            }

            const response = await getFormEntriesByFormName(FORMNAMES.STAFFINFORMATION, filters);
            if (response && response.length > 0) {
                setTopTeachers(response);
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    return (
        <>
            <div className={(isTopTeacher && topTeachers?.length > 0) ? 'py-12 md:py-16 px-4 md:px-6 bg-gray-50' : ""}>

                {/* Heading */}
                {(isTopTeacher && topTeachers?.length > 0) && <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-10"
                >
                    <h2 className="text-3xl md:text-4xl text-primary mb-2">
                        {t(I18N_KEYS.TOPTEACHERTITLE)}
                    </h2>
                    <div className="w-20 h-1 bg-secondary mx-auto mb-3"></div>
                    {schoolDetails?.teacher_subtitle &&
                        <p className="text-gray-600 text-sm md:text-base">
                            {schoolDetails?.teacher_subtitle}
                        </p>
                    }
                </motion.div>}

                {!isTopTeacher && (
                    <>
                        <section className="relative bg-cover bg-center py-36 px-6">
                            <img
                                src={mediaSource(schoolDetails?.dignataries?.fileUrl)}
                                alt={"Page banner"}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        </section>
                        <Breadcrumb />
                    </>
                )}

                {topTeachers?.length > 0 && <section className="py-2 md:py-4 px-4 md:px-6">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 mb-8">
                            {topTeachers?.map((teacher, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    className="group cursor-pointer max-w-[260px] mx-auto w-full"
                                    onClick={() => setSelectedTeacher(teacher)}
                                >
                                    {/* Teacher Card */}
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all h-full flex flex-col pt-6">

                                        {/* Profile Image - Centered at Top */}
                                        <div className="flex justify-center mb-3 px-4">
                                            <div className="w-32 h-32 rounded-full border border-gray-100 overflow-hidden bg-gray-50">
                                                <img
                                                    src={mediaSource(teacher?.payload?.profile_photo?.fileUrl)}
                                                    alt={teacher?.payload?.teacher_name || "Teacher"}
                                                    onError={(e) => mediaSource(e)}
                                                    className="w-full h-full object-cover object-top"
                                                />
                                            </div>
                                        </div>

                                        {/* Content Section */}
                                        <div className="px-5 pb-6 flex-1 flex flex-col items-center text-center">
                                            {/* Name */}
                                            {teacher?.payload?.full_name && (
                                                <div className="flex items-center gap-1 mb-1">
                                                    <h3 className="text-base text-gray-900 line-clamp-1">
                                                        {teacher?.payload?.full_name}
                                                    </h3>
                                                </div>
                                            )}

                                            {/* Original Content Fields */}
                                            <div className="flex-1 w-full space-y-1 mb-4">
                                                {teacher?.payload?.qualification && (
                                                    <p className="text-sm text-gray-600 line-clamp-2 min-h-[20px]">
                                                        {teacher?.payload?.qualification.split(',')[0]}
                                                    </p>
                                                )}

                                                <div className="pt-2 flex flex-col items-center gap-1">
                                                    {teacher?.payload?.experience && (
                                                        <div className="flex items-center gap-1.5 text-gray-500 text-[11px]">
                                                            <FaBriefcase className="text-[10px]" />
                                                            <span>{teacher?.payload?.experience} {t(I18N_KEYS.YEARSOFEXP)}</span>
                                                        </div>
                                                    )}
                                                    {teacher?.payload?.achievement && (
                                                        <div className="flex items-center gap-1.5 text-gray-500 text-[11px]">
                                                            <FaTrophy className="text-[10px]" />
                                                            <span className="line-clamp-1">{teacher?.payload?.achievement}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* LinkedIn-style Pill Button */}
                                            <div className="mt-auto w-full">
                                                <button className="w-full py-1.5 px-4 border-2 border-primary text-primary rounded-full hover:bg-primary/5 transition-colors flex items-center justify-center gap-1 text-sm">
                                                    {t(I18N_KEYS.VIEWPROFILE)}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                    </div>

                    {/* Modal for detailed view */}
                    <AnimatePresence>
                        {selectedTeacher && (
                            <motion.div
                                className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setSelectedTeacher(null)}
                            >
                                <button
                                    onClick={() => setSelectedTeacher(null)}
                                    className="absolute top-5 right-5 bg-accent rounded-full w-11 h-11 shadow-xl text-xl font-bold hover:bg-primary hover:text-white"
                                >
                                    &times;
                                </button>
                                <motion.div
                                    className="bg-accent rounded-3xl overflow-hidden shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                                    initial={{ scale: 0.8, y: 40 }}
                                    animate={{ scale: 1, y: 0 }}
                                    exit={{ scale: 0.8, y: 40 }}
                                    transition={{ duration: 0.3 }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="p-8">
                                        {/* Teacher Photo */}
                                        <div className="flex justify-center mb-8">
                                            <div className="relative w-48 h-48 md:w-64 md:h-64">
                                                <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-primary rounded-full p-1">
                                                    <div className="w-full h-full bg-accent rounded-full overflow-hidden border-4 border-accent">
                                                        <img
                                                            src={mediaSource(selectedTeacher?.payload?.profile_photo?.fileUrl)}
                                                            alt={selectedTeacher?.payload?.full_name || "Teacher"}
                                                            className="w-full h-full object-cover object-top"
                                                            onError={(e) => mediaSource(e)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Teacher Details */}
                                        <div className="text-center">
                                            {selectedTeacher?.payload?.full_name &&
                                                <h3 className="text-3xl md:text-4xl font-bold mb-2 text-primary">
                                                    {selectedTeacher?.payload?.full_name}
                                                </h3>
                                            }

                                            {/* Email */}
                                            {selectedTeacher?.payload?.email && (
                                                <div className="flex items-center justify-center gap-2 text-secondary mb-2">
                                                    <span><MdEmail /></span>
                                                    <a
                                                        href={`mailto:${selectedTeacher?.payload?.email}`}
                                                        className="text-secondary hover:underline"
                                                    >
                                                        {selectedTeacher?.payload?.email}
                                                    </a>
                                                </div>
                                            )}
                                            {selectedTeacher?.payload?.subjects_assigned1_ref?.length > 0 && (
                                                <div className="py-2 mb-6 text-primary font-semibold text-lg ">
                                                    <div className="flex flex-wrap gap-2 justify-center cursor-default">
                                                        {selectedTeacher.payload.subjects_assigned1_ref.map((subject, index) => (
                                                            <span
                                                                key={index}
                                                                className="px-4 py-1.5 bg-primary backdrop-blur  text-accent text-xs md:text-sm font-semibold rounded-full shadow-lg"
                                                            >
                                                                {subject?.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}


                                            {/* Info Grid */}
                                            <div className="grid sm:grid-cols-2 gap-4 mb-6 cursor-default">
                                                {selectedTeacher?.payload?.qualification && (
                                                    <div className="flex items-start gap-3 bg-gray-50 rounded-xl px-4 py-3">
                                                        <div className="flex items-center justify-center w-9 h-9 rounded-full text-secondary shrink-0">
                                                            <FaGraduationCap />
                                                        </div>
                                                        <div>
                                                            <h4 className="text-sm font-semibold text-secondary text-left">
                                                                {t(I18N_KEYS.TEACHERQUALIFICATION)}
                                                            </h4>
                                                            <p className="text-sm text-secondary leading-snug text-left">
                                                                {selectedTeacher.payload.qualification}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {selectedTeacher?.payload?.experience && (
                                                    <div className="flex items-start gap-3 bg-gray-50 rounded-xl px-4 py-3">
                                                        <div className="flex items-center justify-center w-9 h-9 rounded-full text-secondary shrink-0">
                                                            <FaBriefcase />
                                                        </div>
                                                        <div>
                                                            <h4 className="text-sm font-semibold text-secondary text-left">
                                                                {t(I18N_KEYS.TEACHEREXP)}
                                                            </h4>
                                                            <p className="text-sm text-secondary leading-snug text-left">
                                                                {selectedTeacher.payload.experience} {t(I18N_KEYS.YEARSOFEXP)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {selectedTeacher?.payload?.achievement && (
                                                    <div className="flex items-start gap-3 bg-gray-50 rounded-xl px-4 py-3">
                                                        <div className="flex items-center justify-center w-9 h-9 rounded-full text-secondary shrink-0">
                                                            <FaTrophy />
                                                        </div>
                                                        <div>
                                                            <h4 className="text-sm font-semibold text-secondary text-left">
                                                                {t(I18N_KEYS.ACHIEVEMENT)}
                                                            </h4>
                                                            <p className="text-sm text-secondary leading-snug text-left">
                                                                {selectedTeacher.payload.achievement}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Description */}
                                            {selectedTeacher?.payload?.about_staff && (
                                                <div className="rounded-xl mb-4 p-4 cursor-default">
                                                    <h4 className="font-semibold text-secondary text-left text-sm">{t(I18N_KEYS.ABOUT)}</h4>
                                                    <p className="text-secondary leading-relaxed text-left text-sm"
                                                        dangerouslySetInnerHTML={{
                                                            __html: selectedTeacher?.payload?.about_staff
                                                        }}
                                                    >
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </section>}

                {(isTopTeacher && topTeachers.length > 0) &&
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center"
                    >
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/topteachers')}
                            className="px-8 py-3 bg-primary text-accent rounded-lg font-semibold text-sm md:text-base hover:bg-primary/90 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-2"
                        >
                            <span>{t(I18N_KEYS.VIEWTEACHERBUTTON)}</span>
                            <span><FaArrowRight /></span>
                        </motion.button>
                    </motion.div>}
            </div>

        </>
    );
}

export default TopTeachers;